import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';

const API_BASE = '/api/documentation/statements';

const todayISO = () => new Date().toISOString().split('T')[0];
const addDays = (dateStr, days) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    return dt.toISOString().split('T')[0];
};
const addMonths = (dateStr, months) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setMonth(dt.getMonth() + months);
    return dt.toISOString().split('T')[0];
};

const RANGE_PRESETS = [
    { id: 'all', label: 'All' },
    { id: 'minus1d', label: '-1d' },
    { id: 'minus3d', label: '-3d' },
    { id: 'minus7d', label: '-7d' },
    { id: 'minus1m', label: '-1M' },
    { id: 'minus3m', label: '-3M' },
    { id: 'range', label: 'Date Range' },
];

const fmtNum = (v) => {
    const n = parseFloat(v);
    if (!n) return '';
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtBal = (v) => {
    if (v > 0) return `${fmtNum(v)}\u00A0DR`;
    if (v < 0) return `(${fmtNum(Math.abs(v))})\u00A0CR`;
    return '0.00';
};
const fmtDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

const th = {
    borderBottom: '1px solid #4a5568', borderRight: '1px solid #4a5568',
    padding: '6px 8px', fontSize: 12, textAlign: 'center',
    background: '#1f4e79', color: '#fff', fontWeight: 'bold', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2,
};
const td = { borderBottom: '1px solid #cbd5e0', borderRight: '1px solid #cbd5e0', padding: '5px 8px', fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };

const RunningStatement = ({ customer, customerShortName, onClose, toastContainerId }) => {
    const notify = useCallback((type, msg) => {
        toast[type](msg, toastContainerId ? { containerId: toastContainerId } : undefined);
    }, [toastContainerId]);

    const [preset, setPreset] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState(todayISO());
    const [rangeApplied, setRangeApplied] = useState(null); // {from,to} for 'range' preset
    const [entries, setEntries] = useState([]);
    const [opening, setOpening] = useState({ tzs: 0, usd: 0 });
    const [displayName, setDisplayName] = useState(customer || '');
    const [bbfDate, setBbfDate] = useState(null); // client opening-balance date (accounts config)
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const resolveRange = useCallback(() => {
        const today = todayISO();
        switch (preset) {
            case 'all':
                // 'All' begins at the customer's balance-brought-forward date
                // once it is known — earlier entries are inside the opening.
                return bbfDate ? { from: bbfDate, to: today } : null;
            case 'minus1d': return { from: addDays(today, -1), to: today };
            case 'minus3d': return { from: addDays(today, -3), to: today };
            case 'minus7d': return { from: addDays(today, -7), to: today };
            case 'minus1m': return { from: addMonths(today, -1), to: today };
            case 'minus3m': return { from: addMonths(today, -3), to: today };
            case 'range': return rangeApplied;
            default: return null;
        }
    }, [preset, rangeApplied, bbfDate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ customer });
            const range = resolveRange();
            if (range?.from) params.set('from', range.from);
            if (range?.to) params.set('to', range.to);
            const res = await fetch(`${API_BASE}/running-statement/?${params}`, { headers: getAuthHeaders(false) });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Failed to load running statement');
            setEntries(data.entries || []);
            setOpening({ tzs: data.opening_tzs || 0, usd: data.opening_usd || 0 });
            setDisplayName(data.customer_name || data.customer_short_name || displayName);
            setBbfDate(data.bbf_date || null);
            setExpandedRows(new Set());
        } catch (e) {
            notify('error', e.message || 'Failed to load running statement');
        } finally {
            setLoading(false);
        }
    }, [customer, resolveRange]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchData(); }, [fetchData]);

    const downloadStatement = async (kind) => {
        try {
            const params = new URLSearchParams({ customer });
            const range = resolveRange();
            if (range?.from) params.set('from', range.from);
            if (range?.to) params.set('to', range.to);
            const res = await fetch(`${API_BASE}/running-statement-${kind}/?${params}`, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error(`Failed to generate ${kind.toUpperCase()}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Running_Statement_${(displayName || 'customer').replace(/\s+/g, '_')}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            notify('error', e.message || 'Download failed');
        }
    };

    const applyCustomRange = () => {
        if (!customFrom || !customTo) { notify('error', 'Select both From and To dates.'); return; }
        if (customFrom > customTo) { notify('error', 'From date must not be after To date.'); return; }
        setRangeApplied({ from: customFrom, to: customTo });
    };
    // Refetch once the applied custom range lands in state
    useEffect(() => { if (preset === 'range' && rangeApplied) fetchData(); }, [rangeApplied]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleExpand = (idx) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    // Running balances — same math as the PDF statement.
    let balTzs = opening.tzs, balUsd = opening.usd;
    const rows = entries.map((e) => {
        balTzs += (e.dr_tzs || 0) - (e.cr_tzs || 0);
        balUsd += (e.dr_usd || 0) - (e.cr_usd || 0);
        return { ...e, balTzs, balUsd };
    });
    const totDrTzs = entries.reduce((s, e) => s + (e.dr_tzs || 0), 0);
    const totCrTzs = entries.reduce((s, e) => s + (e.cr_tzs || 0), 0);
    const totDrUsd = entries.reduce((s, e) => s + (e.dr_usd || 0), 0);
    const totCrUsd = entries.reduce((s, e) => s + (e.cr_usd || 0), 0);

    return (
        <div style={{ padding: '16px 16px 0 16px', background: '#fff', height: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <h3 style={{ margin: '0 0 10px 0', textAlign: 'center', flexShrink: 0 }}>{displayName}</h3>

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, flexShrink: 0 }}>
                {RANGE_PRESETS.map(p => (
                    <button
                        key={p.id}
                        type="button"
                        onClick={() => { setPreset(p.id); if (p.id !== 'range') setRangeApplied(null); }}
                        style={{
                            padding: '4px 12px', fontSize: 12, fontWeight: preset === p.id ? 'bold' : 'normal',
                            border: preset === p.id ? '2px solid #1f4e79' : '1px solid #bbb',
                            borderRadius: 4, background: preset === p.id ? '#dbeafe' : '#f5f5f5', cursor: 'pointer',
                        }}
                    >
                        {p.label}
                    </button>
                ))}
                {preset === 'range' && (
                    <>
                        <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #bbb', borderRadius: 4 }} />
                        <span style={{ fontSize: 12 }}>to</span>
                        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                            style={{ fontSize: 12, padding: '3px 6px', border: '1px solid #bbb', borderRadius: 4 }} />
                        <button type="button" className="add-btn" style={{ background: '#1f4e79' }} onClick={applyCustomRange}>Apply</button>
                    </>
                )}
                <span style={{ flex: 1 }} />
                <button
                    type="button"
                    className="add-btn"
                    style={{ background: '#b91c1c' }}
                    onClick={() => downloadStatement('pdf')}
                >
                    Download PDF
                </button>
                <button
                    type="button"
                    className="add-btn"
                    style={{ background: '#15803d' }}
                    onClick={() => downloadStatement('excel')}
                >
                    Download Excel
                </button>
            </div>

            {loading && <div className="loading-indicator">Loading...</div>}

            {!loading && (
                <div style={{ overflowY: 'auto', overflowX: 'auto', flex: 1, minHeight: 0 }}>
                    <div style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', borderLeft: '1px solid #4a5568', borderTop: '1px solid #4a5568', borderRight: '1px solid #4a5568' }}>
                            <colgroup>
                                <col style={{ width: '4.5%' }} />
                                <col style={{ width: '8.5%' }} />
                                <col style={{ width: '24%' }} />
                                <col style={{ width: '10.5%' }} />
                                <col style={{ width: '10.5%' }} />
                                <col style={{ width: '13.5%' }} />
                                <col style={{ width: '10.5%' }} />
                                <col style={{ width: '10.5%' }} />
                                <col style={{ width: '7.5%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    {['SN', 'DATE', 'PARTICULARS', 'DR AMOUNT TZS', 'CR AMOUNT TZS', 'BALANCE TZS',
                                      'DR AMOUNT USD', 'CR AMOUNT USD', 'BALANCE USD'].map(h => (
                                        <th key={h} style={{ ...th, position: 'static' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                        </table>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', borderLeft: '1px solid #cbd5e0' }}>
                        <colgroup>
                            <col style={{ width: '4.5%' }} />
                            <col style={{ width: '8.5%' }} />
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '10.5%' }} />
                            <col style={{ width: '10.5%' }} />
                            <col style={{ width: '13.5%' }} />
                            <col style={{ width: '10.5%' }} />
                            <col style={{ width: '10.5%' }} />
                            <col style={{ width: '7.5%' }} />
                        </colgroup>
                        <tbody>
                            <tr style={{ background: '#f3f4f6' }}>
                                <td style={{ ...td, textAlign: 'center' }} colSpan={5}><strong>OPENING BALANCE</strong></td>
                                <td style={tdR}><strong>{fmtBal(opening.tzs)}</strong></td>
                                <td style={td} colSpan={2}></td>
                                <td style={tdR}><strong>{fmtBal(opening.usd)}</strong></td>
                            </tr>
                            {rows.map((e, idx) => {
                                const expandable = (e.kind === 'debit_note' || e.kind === 'expenses') && (e.expand || []).length > 0;
                                const isOpen = expandedRows.has(idx);
                                return (
                                    <React.Fragment key={idx}>
                                        <tr style={{ background: idx % 2 ? '#fff' : '#f3e5f5' }}>
                                            <td style={{ ...td, textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={{ ...td, textAlign: 'center' }}>{fmtDate(e.date)}</td>
                                            <td
                                                style={{ ...td, whiteSpace: 'normal', minWidth: 220, cursor: expandable ? 'pointer' : 'default', color: expandable ? '#1d4ed8' : 'inherit' }}
                                                onClick={expandable ? () => toggleExpand(idx) : undefined}
                                                title={expandable ? 'Click to expand breakdown' : undefined}
                                            >
                                                {e.particulars}
                                                {expandable && <span style={{ marginLeft: 6, fontSize: 10 }}>{isOpen ? '▲' : '▼'}</span>}
                                            </td>
                                            <td style={tdR}>{fmtNum(e.dr_tzs)}</td>
                                            <td style={tdR}>{fmtNum(e.cr_tzs)}</td>
                                            <td style={tdR}>{fmtBal(e.balTzs)}</td>
                                            <td style={tdR}>{fmtNum(e.dr_usd)}</td>
                                            <td style={tdR}>{fmtNum(e.cr_usd)}</td>
                                            <td style={tdR}>{fmtBal(e.balUsd)}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan={9} style={{ ...td, padding: '4px 0', background: '#f8fafc' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                                                        <colgroup>
                                                            <col style={{ width: '4.5%' }} />
                                                            <col style={{ width: '8.5%' }} />
                                                            <col style={{ width: '24%' }} />
                                                            <col style={{ width: '10.5%' }} />
                                                            <col style={{ width: '10.5%' }} />
                                                            <col style={{ width: '13.5%' }} />
                                                            <col style={{ width: '10.5%' }} />
                                                            <col style={{ width: '10.5%' }} />
                                                            <col style={{ width: '7.5%' }} />
                                                        </colgroup>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ ...th, background: '#3949ab' }}>SN</th>
                                                                <th style={{ ...th, background: '#3949ab' }}>DATE</th>
                                                                <th style={{ ...th, background: '#3949ab', textAlign: 'left' }}>PARTICULARS</th>
                                                                <th style={{ ...th, background: '#3949ab', textAlign: 'right' }}>DR AMOUNT TZS</th>
                                                                <th style={{ ...th, background: '#3949ab' }}></th>
                                                                <th style={{ ...th, background: '#3949ab' }}></th>
                                                                <th style={{ ...th, background: '#3949ab', textAlign: 'right' }}>DR AMOUNT USD</th>
                                                                <th style={{ ...th, background: '#3949ab' }}></th>
                                                                <th style={{ ...th, background: '#3949ab' }}></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {e.expand.map((line, li) => (
                                                                <tr key={li} style={{ background: li % 2 ? '#fff' : '#e3f2fd' }}>
                                                                    <td style={{ ...td, textAlign: 'center' }}>{li + 1}</td>
                                                                    <td style={{ ...td, textAlign: 'center' }}>{fmtDate(line.date)}</td>
                                                                    <td style={{ ...td, whiteSpace: 'normal' }}>{line.label}</td>
                                                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(line.tzs)}</td>
                                                                    <td style={td}></td>
                                                                    <td style={td}></td>
                                                                    <td style={{ ...td, textAlign: 'right' }}>{fmtNum(line.usd)}</td>
                                                                    <td style={td}></td>
                                                                    <td style={td}></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>No entries in this period.</td></tr>
                            )}
                            <tr style={{ background: '#e5e7eb', borderTop: '2px solid #4a5568' }}>
                                <td style={{ ...td, textAlign: 'center' }} colSpan={3}><strong>FINAL BALANCE</strong></td>
                                <td style={tdR}><strong>{fmtNum(totDrTzs)}</strong></td>
                                <td style={tdR}><strong>{fmtNum(totCrTzs)}</strong></td>
                                <td style={tdR}><strong>{fmtBal(balTzs)}</strong></td>
                                <td style={tdR}><strong>{fmtNum(totDrUsd)}</strong></td>
                                <td style={tdR}><strong>{fmtNum(totCrUsd)}</strong></td>
                                <td style={tdR}><strong>{fmtBal(balUsd)}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RunningStatement;
